import { takeEvery, put, call, take } from 'redux-saga/effects';
import {
  createPromiseAction,
  resolvePromiseAction,
  // rejectPromiseAction,
} from '@adobe/redux-saga-promise';
import {
  VIEWING_DOWNLOAD_DOCUMENT_BEGIN,
  VIEWING_DOWNLOAD_DOCUMENT_SUCCESS,
  VIEWING_DOWNLOAD_DOCUMENT_FAILURE,
  VIEWING_DOWNLOAD_DOCUMENT_PROGRESS,
} from './constants';
import api from 'common/api';
import { withCurrentCaseId } from 'common/selectors';
import { mergeItemImmutable, addItemImmutable } from 'utils/arrays';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';

const isJsonBlob = (blob) =>
  typeof Blob !== 'undefined' &&
  blob instanceof Blob &&
  typeof blob.type === 'string' &&
  blob.type.includes('application/json');

const QUEUED_RESPONSE_MAX_BYTES = 256 * 1024;

const canContainQueuedPayload = (document) =>
  !!document?.blob &&
  typeof document.blob.text === 'function' &&
  (isJsonBlob(document.blob) || document.blob.size <= QUEUED_RESPONSE_MAX_BYTES);

function* getQueuedDownloadResponse(document) {
  if (!canContainQueuedPayload(document)) {
    return null;
  }

  try {
    const bodyText = yield call([document.blob, document.blob.text]);
    const parsedBody = JSON.parse(bodyText);

    if (String(parsedBody?.status || '').trim().toLowerCase() === 'queued') {
      return parsedBody;
    }
  } catch (error) {
    console.error('Failed to parse queued download response', error);
  }

  return null;
}

export const downloadDocument = createPromiseAction(VIEWING_DOWNLOAD_DOCUMENT_BEGIN);
const DOWNLOAD_SCOPE_FILE_IDS = 'fileIds';
const DOWNLOAD_SCOPE_FOLDER = 'folder';
const DOWNLOAD_SCOPE_FOLDER_TREE = 'folderTree';
const DOWNLOAD_NAMING_STRATEGY_DEFAULT = 'default';
const DOWNLOAD_NAMING_STRATEGY_ORIGINAL = 'original';
const DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME = 'folderSpecificName';
const DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_ONLY = 'bundleTabOnly';
const DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_FILENAME = 'bundleTabFilename';

const normalizeId = (value) => (value === null || typeof value === 'undefined' ? '' : String(value).trim());
const normalizeKey = (value) => normalizeId(value).toLowerCase();

const normalizeIdList = (values = []) =>
  Array.from(
    new Set(
      (values || [])
        .map((value) => normalizeId(value))
        .filter(Boolean),
    ),
  );

const resolveScopeType = ({ scopeType, fileIds, folderId, folderHasChildren }) => {
  if (
    [DOWNLOAD_SCOPE_FILE_IDS, DOWNLOAD_SCOPE_FOLDER, DOWNLOAD_SCOPE_FOLDER_TREE].includes(scopeType)
  ) {
    return scopeType;
  }

  if (fileIds.length) {
    return DOWNLOAD_SCOPE_FILE_IDS;
  }

  if (folderId) {
    return folderHasChildren ? DOWNLOAD_SCOPE_FOLDER_TREE : DOWNLOAD_SCOPE_FOLDER;
  }

  return DOWNLOAD_SCOPE_FILE_IDS;
};

const resolveNamingStrategy = ({ namingStrategy, namingMode, filenameSelector, fileNameSelector }) => {
  const normalizedNamingStrategy = normalizeKey(namingStrategy || namingMode);
  if (normalizedNamingStrategy === DOWNLOAD_NAMING_STRATEGY_DEFAULT) {
    return DOWNLOAD_NAMING_STRATEGY_DEFAULT;
  }
  if (normalizedNamingStrategy === DOWNLOAD_NAMING_STRATEGY_ORIGINAL) {
    return DOWNLOAD_NAMING_STRATEGY_ORIGINAL;
  }
  if (
    normalizedNamingStrategy === normalizeKey(DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME) ||
    normalizedNamingStrategy === 'folderspecific'
  ) {
    return DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME;
  }
  if (
    normalizedNamingStrategy === normalizeKey(DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_ONLY) ||
    normalizedNamingStrategy === 'bundletabonly' ||
    normalizedNamingStrategy === 'bundletab'
  ) {
    return DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_ONLY;
  }
  if (
    normalizedNamingStrategy === normalizeKey(DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_FILENAME) ||
    normalizedNamingStrategy === 'bundletabfilename'
  ) {
    return DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_FILENAME;
  }

  const resolvedFileNameSelector = normalizeKey(filenameSelector || fileNameSelector);
  if (resolvedFileNameSelector === 'original') {
    return DOWNLOAD_NAMING_STRATEGY_ORIGINAL;
  }
  if (resolvedFileNameSelector === 'bundletab' || resolvedFileNameSelector === 'bundletabonly') {
    return DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_ONLY;
  }
  if (resolvedFileNameSelector === 'bundletabfilename') {
    return DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_FILENAME;
  }
  if (
    resolvedFileNameSelector === normalizeKey(DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME) ||
    resolvedFileNameSelector === 'folderspecific'
  ) {
    return DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME;
  }

  // Unsupported legacy selectors (e.g. discoveryId/externalId/trialviewId) map to default.
  return DOWNLOAD_NAMING_STRATEGY_DEFAULT;
};

const resolvePrependBundleTab = ({
  prependBundleTab,
  useTabNumbers,
  namingStrategy,
  namingMode,
  filenameSelector,
  fileNameSelector,
}) => {
  if (typeof prependBundleTab === 'boolean') {
    return prependBundleTab;
  }

  const normalizedNamingStrategy = normalizeKey(namingStrategy || namingMode);
  if (
    normalizedNamingStrategy === normalizeKey(DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_FILENAME) ||
    normalizedNamingStrategy === 'bundletabfilename'
  ) {
    return true;
  }

  const resolvedFileNameSelector = normalizeKey(filenameSelector || fileNameSelector);
  if (resolvedFileNameSelector === 'bundletabfilename') {
    return true;
  }

  return !!useTabNumbers;
};

const buildNaming = ({
  namingStrategy,
  namingMode,
  filenameSelector,
  fileNameSelector,
  prependBundleTab,
  useTabNumbers,
  folderId,
}) => {
  let resolvedNamingStrategy = resolveNamingStrategy({
    namingStrategy,
    namingMode,
    filenameSelector,
    fileNameSelector,
  });
  let resolvedPrependBundleTab = resolvePrependBundleTab({
    prependBundleTab,
    useTabNumbers,
    namingStrategy,
    namingMode,
    filenameSelector,
    fileNameSelector,
  });

  // Legacy bundleTabFilename maps to folderSpecificName + prependBundleTab=true.
  if (resolvedNamingStrategy === DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_FILENAME) {
    resolvedNamingStrategy = DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME;
    resolvedPrependBundleTab = true;
  }

  const normalizedFolderId = normalizeId(folderId);
  if (
    !normalizedFolderId &&
    (
      resolvedNamingStrategy === DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME ||
      resolvedNamingStrategy === DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_ONLY ||
      resolvedPrependBundleTab
    )
  ) {
    resolvedNamingStrategy = DOWNLOAD_NAMING_STRATEGY_DEFAULT;
    resolvedPrependBundleTab = false;
  }

  const naming = {
    strategy: resolvedNamingStrategy,
  };

  if (resolvedPrependBundleTab) {
    naming.prependBundleTab = true;
  }

  if (
    normalizedFolderId &&
    (
      resolvedNamingStrategy === DOWNLOAD_NAMING_STRATEGY_FOLDER_SPECIFIC_NAME ||
      resolvedNamingStrategy === DOWNLOAD_NAMING_STRATEGY_BUNDLE_TAB_ONLY ||
      resolvedPrependBundleTab
    )
  ) {
    naming.folderId = normalizedFolderId;
  }

  return naming;
};

// worker Saga: will be fired on VIEWING_DOWNLOAD_DOCUMENT_BEGIN actions
export function* doDownloadDocument(action) {
  const {
    payload: {
      caseId,
      fileId,
      fileIds,
      original,
      print,
      version,
      versionId,
      burnId,
      burnExternalId,
      burnDiscoveryId,
      burnDisclosureId,
      burnExhibitNumber,
      variant,
      burnCrossRef,
      burncrossref,
      hyperlink,
      publichyperlink,
      filenameSelector,
      fileNameSelector,
      scopeType,
      namingStrategy,
      namingMode,
      prependBundleTab,
      folderHasChildren,
      folderId,
      includeMerge,
      useTabNumbers,
      useBundleTabPage,
      legacy,
    },
  } = action;

  const resolvedBurnCrossRef =
    typeof burncrossref !== 'undefined' ? burncrossref : burnCrossRef;
  let channel;

  if (legacy) {
    const legacyDownloadUrl = original
      ? original
      : `/cases/${caseId}/files/${fileId}${
          version
            ? `?version=${version}&versionId=${versionId}&burnId=${burnId}&burnExternalId=${burnExternalId}&burnCrossRef=${resolvedBurnCrossRef}&hyperlink=${hyperlink}&publichyperlink=${publichyperlink}&filenameSelector=${filenameSelector}&folderId=${folderId}`
            : variant
              ? `?variant=${variant}`
              : ''
        }`;

    channel = yield call(api.createDownloadFileChannel, legacyDownloadUrl, !!original);
  } else {
    const resolvedFileIds = normalizeIdList(fileIds || (fileId ? [fileId] : []));
    const normalizedFolderId = normalizeId(folderId);
    const resolvedScopeType = resolveScopeType({
      scopeType,
      fileIds: resolvedFileIds,
      folderId: normalizedFolderId,
      folderHasChildren: !!folderHasChildren,
    });
    const naming = buildNaming({
      namingStrategy,
      namingMode,
      filenameSelector,
      fileNameSelector,
      prependBundleTab,
      useTabNumbers,
      folderId: normalizedFolderId,
    });
    const resolvedVersionId = normalizeId(versionId);
    const resolvedVersion = resolvedVersionId ? '' : version || 'original';
    const requestBody = {
      scope: {
        type: resolvedScopeType,
        fileIds: resolvedScopeType === DOWNLOAD_SCOPE_FILE_IDS ? resolvedFileIds : [],
        folderId:
          resolvedScopeType === DOWNLOAD_SCOPE_FOLDER || resolvedScopeType === DOWNLOAD_SCOPE_FOLDER_TREE
            ? normalizedFolderId
            : '',
      },
      naming,
      variant: variant || 'noRing',
      version: resolvedVersion,
      ...(resolvedVersionId ? { versionId: resolvedVersionId } : {}),
      burnId: !!burnId,
      burnDiscoveryId: !!burnDiscoveryId,
      burnDisclosureId: !!burnDisclosureId,
      burnExhibitNumber: !!burnExhibitNumber,
      burnExternalId: !!burnExternalId,
      burncrossref: !!resolvedBurnCrossRef,
      hyperlink: !!hyperlink,
      publichyperlink: !!publichyperlink,
      includeMerge: !!includeMerge,
      useTabNumbers: !!useTabNumbers,
      useBundleTabPage: !!useBundleTabPage,
    };

    channel = yield call(api.createDownloadFileChannel, `/cases/${caseId}/files/downloads`, false, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
  }

  while (true) {
    const { progress = 0, error, success, response: document } = yield take(channel);

    if (error) {
      yield put({
        type: VIEWING_DOWNLOAD_DOCUMENT_FAILURE,
        data: {
          file: {
            fileId,
            error,
            original,
            print,
            ...(original
              ? { originalProgress: null }
              : print
                ? { printProgress: null }
                : { progress: null }),
          },
        },
      });
      // return yield call(rejectPromiseAction, action, res.error);
      yield call(resolvePromiseAction, action);

      return;
    }

    if (success) {
      const queuedDownloadResponse = yield call(getQueuedDownloadResponse, document);
      const resolvedResponse = queuedDownloadResponse || document;

      yield put({
        type: VIEWING_DOWNLOAD_DOCUMENT_SUCCESS,
        data: {
          file: {
            fileId,
            success,
            fileName: document?.fileName,
            original,
            print,
            ...(original
              ? { originalProgress: null }
              : print
                ? { printProgress: null }
                : { progress: null }),
          },
        },
      });

      yield call(resolvePromiseAction, action, resolvedResponse);
      return;
    }

    yield put({
      type: VIEWING_DOWNLOAD_DOCUMENT_PROGRESS,
      data: {
        file: {
          fileId,
          original,
          print,
          ...(original
            ? { originalProgress: progress }
            : print
              ? { printProgress: progress }
              : { progress }),
        },
      },
    });
  }
}

/*
  Alternatively you may use takeEvery.

  takeLatest does not allow concurrent requests. If an action gets
  dispatched while another is already pending, that pending one is cancelled
  and only the latest one will be run.
*/
export function* watchDownloadDocument() {
  yield takeEvery(downloadDocument, withCurrentCaseId(doDownloadDocument));
}

export function useDownloadDocument() {
  const dispatch = useDispatch();

  const { downloadDocumentsFeedback, downloadedDocuments } = useSelector(
    (state) => ({
      downloadDocumentsFeedback: state.viewing.downloadDocumentsFeedback,
      downloadedDocuments: state.viewing.downloadedDocuments,
    }),
    shallowEqual,
  );

  const boundAction = useCallback(
    (...args) => {
      return dispatch(downloadDocument(...args));
    },
    [dispatch],
  );

  return {
    downloadedDocuments,
    downloadDocument: boundAction,
    downloadDocumentsFeedback,
  };
}

// Redux reducer
export function reducer(state, action) {
  const getKey = ({ original, print, fileId }) =>
    `${fileId}-${original ? 'downloadOriginal' : print ? 'print' : 'download'}-Feedback`;
  const getErrorMessage = ({ original, print }) =>
    original
      ? 'feedback.downloadOriginalFailure'
      : print
        ? 'feedback.printFailure'
        : 'feedback.downloadFailure';
  const getProgressMessage = ({ original, print }) =>
    original
      ? 'feedback.downloadOriginalProgress'
      : print
        ? 'feedback.printProgress'
        : 'feedback.downloadProgress';
  const getProgress = ({ progress, originalProgress, printProgress, original, print }) =>
    Math.round(original ? originalProgress : print ? printProgress : progress);
  switch (action.type) {
    case VIEWING_DOWNLOAD_DOCUMENT_BEGIN + '.TRIGGER':
      return {
        ...state,
        [getKey(action.payload)]: null,
      };

    case VIEWING_DOWNLOAD_DOCUMENT_SUCCESS:
      const indexS = state.downloadedDocuments.findIndex(
        (item) => item.fileId === action.data.file.fileId,
      );
      return {
        ...state,
        downloadedDocuments:
          indexS > -1
            ? mergeItemImmutable(state.downloadedDocuments, action.data.file, indexS)
            : addItemImmutable(state.downloadedDocuments, action.data.file),
        [getKey(action.data.file)]: {
          message: 'feedback.downloadSuccess',
          error: false,
        },
      };

    case VIEWING_DOWNLOAD_DOCUMENT_PROGRESS:
      const indexP = state.downloadedDocuments.findIndex(
        (item) => item.fileId === action.data.file.fileId,
      );
      return {
        ...state,
        downloadedDocuments:
          indexP > -1
            ? mergeItemImmutable(state.downloadedDocuments, action.data.file, indexP)
            : addItemImmutable(state.downloadedDocuments, action.data.file),
        [getKey(action.data.file)]: {
          message: getProgressMessage(action.data.file),
          progress: getProgress(action.data.file),
          params: {
            file: action.data.file.fileId,
            progress: getProgress(action.data.file),
          },
          infoSticky: true,
          error: false,
        },
      };

    case VIEWING_DOWNLOAD_DOCUMENT_FAILURE:
      const indexF = state.downloadedDocuments.findIndex(
        (item) => item.fileId === action.data.file.fileId,
      );
      return {
        ...state,
        downloadDocumentsFeedback: { error: true },
        downloadedDocuments:
          indexF > -1
            ? mergeItemImmutable(state.downloadedDocuments, action.data.file, indexF)
            : addItemImmutable(state.downloadedDocuments, action.data.file),
        [getKey(action.data.file)]: {
          message: getErrorMessage(action.data.file),
          params: { file: action.data.file.fileId },
          error: action.data.file.error,
        },
      };

    default:
      return state;
  }
}
